import React, { useEffect, useState } from "react";
import { X, Calculator, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api, API, getToken, formatTL, fmtDate } from "@/lib/api";
import { SnapCard, ExitRow } from "./shared";

export default function ExitPdfModal({ person, onClose }) {
  const [kalanIzin, setKalanIzin] = useState(0);
  const [maasOran, setMaasOran] = useState(1.0);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/personnel/${person.id}/exit-compensation`, {
        params: { kalan_izin: kalanIzin || 0, maas_oran: maasOran || 1.0 },
      });
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hesaplama yapılamadı");
      setPreview(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const t = setTimeout(() => refresh(), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [kalanIzin, maasOran]);

  const download = async () => {
    setDownloading(true);
    try {
      const token = getToken();
      const url = `${API}/personnel/${person.id}/exit-pdf?kalan_izin=${kalanIzin || 0}&maas_oran=${maasOran || 1.0}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      let fname = `cikis_tazminati_${person.sicil_no}.pdf`;
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/i);
      if (m) fname = m[1];
      const blob = await res.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = u; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(u);
      toast.success("PDF indirildi");
    } catch {
      toast.error("PDF indirilemedi");
    } finally { setDownloading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-2 sm:p-4" data-testid="exit-pdf-modal">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-2xl max-h-[92vh] flex flex-col">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg font-bold flex items-center gap-2">
              <Calculator size={18} className="text-rose-700" /> Çıkış Tazminatı · {person.ad_soyad}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              TC {person.sicil_no} · İşe başlama {fmtDate(person.ise_baslama_tarihi)} · Çıkış {fmtDate(person.ise_cikis_tarihi) || "—"}
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">
                Kalan Yıllık İzin (gün)
              </label>
              <input
                data-testid="exit-kalan-izin"
                type="number"
                step="0.5"
                min="0"
                value={kalanIzin}
                onChange={(e) => setKalanIzin(Number(e.target.value))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono-tab focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">
                Son Ay Çalışma Oranı (0-1)
              </label>
              <input
                data-testid="exit-maas-oran"
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={maasOran}
                onChange={(e) => setMaasOran(Number(e.target.value))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono-tab focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
            </div>
          </div>

          {loading && <div className="text-center py-6 text-slate-400 text-sm">Hesaplanıyor...</div>}
          {!loading && preview && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <SnapCard label="Toplam Hizmet" value={`${preview.tam_yil} yıl ${preview.artan_ay} ay`} tone="slate" />
                <SnapCard label="Çıkış Sebebi" value={preview.cikis_sebebi_label} tone="indigo" />
                <SnapCard label="İhbar Hakkı" value={`${preview.ihbar_hafta} hafta`} tone="slate" />
                <SnapCard label="Aylık Brüt" value={formatTL(preview.aylik_brut)} tone="slate" />
              </div>

              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-900 text-white">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider">Kalem</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider">Brüt Tutar (TL)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <ExitRow label="Kıdem Tazminatı" value={preview.kidem_tazminati} />
                    <ExitRow label={`İhbar Tazminatı (${preview.ihbar_hafta} hafta)`} value={preview.ihbar_tazminati} />
                    <ExitRow label={`Yıllık İzin Bakiye (${preview.kalan_izin_gun} gün)`} value={preview.izin_bakiye_tutar} />
                    <ExitRow label={`Son Dönem Maaşı (×${preview.son_maas_oran.toFixed(2)})`} value={preview.son_maas} />
                  </tbody>
                  <tfoot>
                    <tr className="bg-emerald-50 border-t-2 border-emerald-200">
                      <td className="px-3 py-2.5 font-bold text-emerald-900">TOPLAM BRÜT ÖDENECEK</td>
                      <td className="px-3 py-2.5 text-right font-mono-tab font-bold text-emerald-900 text-base">
                        {formatTL(preview.toplam)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <p className="text-[11px] text-slate-500">
                Not: Brüt tutarlar. SGK tavanı ve damga vergisi uygulanmamıştır. İhbar hakkı 4857 sayılı İş Kanunu md.17 basamaklarıyla hesaplanır.
              </p>
            </>
          )}
        </div>

        <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold hover:bg-slate-50">Kapat</button>
          <button
            data-testid="exit-pdf-download"
            onClick={download}
            disabled={downloading || !preview}
            className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <FileDown size={14} /> {downloading ? "İndiriliyor..." : "PDF İndir"}
          </button>
        </div>
      </div>
    </div>
  );
}
